import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import JSZip from "jszip";
import { Download, FileDown, Eye, AlertTriangle } from "lucide-react";
import { useCompany } from "../../context/CompanyContext";
import {
  generatePayslipPDF,
  generatePayslipBlob,
} from "../components/PayslipPDF";
import {
  getPayrollRunDetails,
  getPayrollRunLines,
  getPayslipData,
  previewPayrollRun,
} from "../payrollService";

function formatMoney(value) {
  return Number(value || 0).toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

function getPayeLineFromBreakdown(lines = []) {
  return (
    lines.find((line) => {
      const code = String(line.component_code || "").toUpperCase();
      const name = String(line.component_name || "").toLowerCase();

      return (
        code === "PAYE" ||
        code.includes("PAYE") ||
        name.includes("paye") ||
        name.includes("pay as you earn") ||
        (name.includes("tax") && !name.includes("tax relief"))
      );
    }) || null
  );
}

function buildPayeComputationFromMetadata(payeLine) {
  const meta = payeLine?.metadata || {};

  return {
    calculation_base: Number(meta.calculation_base || 0),
    monthly_gross_income: Number(meta.monthly_gross_income || 0),
    pay_component_deductions: Number(meta.pay_component_deductions || 0),
    annual_rent_relief: Number(meta.annual_rent_relief || 0),
    monthly_rent_relief: Number(meta.monthly_rent_relief || 0),
    monthly_taxable_income: Number(meta.monthly_taxable_income || 0),
    annual_taxable_income: Number(meta.annual_taxable_income || 0),
    annual_paye_payable: Number(meta.annual_paye_payable || 0),
    monthly_paye_payable:
      Number(meta.monthly_paye_payable || 0) || Number(payeLine?.amount || 0),
    tax_breakdown: Array.isArray(meta.tax_breakdown) ? meta.tax_breakdown : [],
    notes: meta.notes || "",
  };
}

function PayeBreakdownCard({ payeLine, title = "PAYE Breakdown" }) {
  if (!payeLine) {
    return <p>No PAYE/tax breakdown found for this employee.</p>;
  }

  const paye = buildPayeComputationFromMetadata(payeLine);

  return (
    <>
      <div className="payroll-card">
        <div className="payroll-card-header">
          <h3>{title}</h3>
        </div>

        <div className="payroll-table-wrap">
          <table className="payroll-table">
            <thead>
              <tr>
                <th>PAYE Computation Item</th>
                <th>Amount</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>Monthly Gross Income</td>
                <td>{formatMoney(paye.monthly_gross_income)}</td>
              </tr>
              <tr>
                <td>Less: Pay Component Deductions</td>
                <td>{formatMoney(paye.pay_component_deductions)}</td>
              </tr>
              <tr>
                <td>Less: Monthly Rent Relief</td>
                <td>{formatMoney(paye.monthly_rent_relief)}</td>
              </tr>
              <tr>
                <td>Monthly Taxable Income</td>
                <td>{formatMoney(paye.monthly_taxable_income)}</td>
              </tr>
              <tr>
                <td>Annual Taxable Income (Monthly Taxable × 12)</td>
                <td>{formatMoney(paye.annual_taxable_income)}</td>
              </tr>
              <tr>
                <td>Annual PAYE Payable</td>
                <td>{formatMoney(paye.annual_paye_payable)}</td>
              </tr>
              <tr>
                <td>Monthly PAYE Payable</td>
                <td>{formatMoney(paye.monthly_paye_payable)}</td>
              </tr>
              <tr>
                <td>Calculation Base</td>
                <td>{formatMoney(paye.calculation_base)}</td>
              </tr>
            </tbody>
          </table>
        </div>

        {paye.notes ? (
          <div className="payroll-alert warning" style={{ marginTop: 12 }}>
            {paye.notes}
          </div>
        ) : null}
      </div>

      <div className="payroll-card">
        <div className="payroll-card-header">
          <h3>Progressive Tax Bands</h3>
        </div>

        {Array.isArray(paye.tax_breakdown) && paye.tax_breakdown.length ? (
          <div className="payroll-table-wrap">
            <table className="payroll-table">
              <thead>
                <tr>
                  <th>Band</th>
                  <th>Rate %</th>
                  <th>Taxable Amount</th>
                  <th>Tax Amount</th>
                </tr>
              </thead>
              <tbody>
                {paye.tax_breakdown.map((band, idx) => (
                  <tr key={`${band.label}-${idx}`}>
                    <td>{band.label}</td>
                    <td>{formatMoney(band.rate || 0)}</td>
                    <td>{formatMoney(band.taxable_amount || 0)}</td>
                    <td>{formatMoney(band.tax_amount || 0)}</td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr>
                  <th colSpan="3">Annual PAYE Total</th>
                  <th>{formatMoney(paye.annual_paye_payable)}</th>
                </tr>
                <tr>
                  <th colSpan="3">Monthly PAYE Charged</th>
                  <th>{formatMoney(paye.monthly_paye_payable)}</th>
                </tr>
              </tfoot>
            </table>
          </div>
        ) : (
          <p>No tax band detail available for this PAYE rule.</p>
        )}
      </div>
    </>
  );
}

export default function PayrollRunDetails() {
  const { runId } = useParams();
  const [msg, setMsg] = useState("");
  const [run, setRun] = useState(null);
  const [employees, setEmployees] = useState([]);
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [lines, setLines] = useState([]);
  const { companyId, company } = useCompany();
  const [bulkDownloading, setBulkDownloading] = useState(false);
  const [previewData, setPreviewData] = useState(null);
  const [previewing, setPreviewing] = useState(false);
  const [selectedPreviewEmployee, setSelectedPreviewEmployee] = useState(null);
  const [selectedProcessedEmployee, setSelectedProcessedEmployee] =
    useState(null);
  const [selectedProcessedBreakdown, setSelectedProcessedBreakdown] = useState(
    []
  );

  useEffect(() => {
    async function load() {
      if (!runId) return;
      try {
        const data = await getPayrollRunDetails(runId);
        setRun(data.run);
        setEmployees(data.employees || []);
      } catch (err) {
        setMsg(err.message || "Failed to load payroll run details");
      }
    }
    load();
  }, [runId]);

  function getPayeLine(previewEmployee) {
    return getPayeLineFromBreakdown(previewEmployee?.breakdown || []);
  }

  function getProcessedPayeLine(lines = []) {
    return getPayeLineFromBreakdown(lines);
  }

  async function handleViewProcessedDetails(row) {
    try {
      setMsg("");

      const { breakdown } = await getPayslipData(
        companyId,
        run.id,
        row.employee_id
      );

      setSelectedProcessedEmployee(row);
      setSelectedProcessedBreakdown(breakdown || []);
    } catch (err) {
      console.error(err);
      setMsg(err.message || "Failed to load processed payroll details");
    }
  }

  async function showLines(employee) {
    try {
      setSelectedEmployee(employee);
      const data = await getPayrollRunLines(runId, employee.employee_id);
      setLines(data);
    } catch (err) {
      setMsg(err.message || "Failed to load payroll lines");
    }
  }

  async function handleDownloadPayslip(row) {
    try {
      setMsg("");

      const { employeeSummary, breakdown } = await getPayslipData(
        companyId,
        run.id,
        row.employee_id
      );

      generatePayslipPDF({
        company,
        employee: employeeSummary,
        run,
        breakdown,
      });
    } catch (err) {
      console.error(err);
      setMsg(err.message || "Failed to generate payslip");
    }
  }

  async function handleBulkDownloadPayslips() {
    try {
      setBulkDownloading(true);
      setMsg("");

      if (!employees?.length) {
        throw new Error("No employees found for this payroll run");
      }

      const zip = new JSZip();

      for (const row of employees) {
        const { employeeSummary, breakdown } = await getPayslipData(
          companyId,
          run.id,
          row.employee_id
        );

        const { fileName, blob } = generatePayslipBlob({
          company,
          employee: employeeSummary,
          run,
          breakdown,
        });

        zip.file(fileName, blob);
      }

      const zipBlob = await zip.generateAsync({ type: "blob" });
      const url = window.URL.createObjectURL(zipBlob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `Payslips_${String(run?.run_name || "Payroll_Run").replace(
        /[^\w-]+/g,
        "_"
      )}.zip`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);

      setMsg("Payslips downloaded successfully.");
    } catch (err) {
      console.error(err);
      setMsg(err.message || "Failed to download all payslips");
    } finally {
      setBulkDownloading(false);
    }
  }

  async function handlePreviewPayroll() {
    try {
      setPreviewing(true);
      setMsg("");

      const data = await previewPayrollRun(companyId, run.id);

      setPreviewData(data);
      setMsg("Preview generated successfully.");
    } catch (err) {
      console.error(err);
      setMsg(err.message || "Failed to preview payroll");
    } finally {
      setPreviewing(false);
    }
  }

  return (
    <div className="payroll-page">
      {msg ? <div className="payroll-alert error">{msg}</div> : null}

      <div className="payroll-card">
        <div className="payroll-card-header">
          <div>
            <h3>{run?.run_name || "Payroll Run"}</h3>
            <p>
              Period: {run?.payroll_periods?.period_name || "-"} | Status:{" "}
              <span className={`pill ${run?.status || "draft"}`}>
                {run?.status || "-"}
              </span>
            </p>
          </div>
        </div>

        {previewData && (
          <div className="payroll-card">
            <div className="payroll-card-header">
              <h3>Payroll Preview Summary</h3>
            </div>

            <div className="payroll-grid payroll-grid-4">
              <div className="payroll-summary-card">
                <p>Employees</p>
                <h3>{previewData.employee_count}</h3>
              </div>

              <div className="payroll-summary-card">
                <p>Gross Pay</p>
                <h3>{formatMoney(previewData.totals.gross_total)}</h3>
              </div>

              <div className="payroll-summary-card">
                <p>Deductions</p>
                <h3>{formatMoney(previewData.totals.deduction_total)}</h3>
              </div>

              <div className="payroll-summary-card">
                <p>Net Pay</p>
                <h3>{formatMoney(previewData.totals.net_total)}</h3>
              </div>
            </div>
          </div>
        )}

        {previewData?.validations?.warning_count > 0 && (
          <div className="payroll-alert warning">
            <AlertTriangle size={16} />
            {previewData.validations.warning_count} issue(s) detected. Review
            before processing.
          </div>
        )}

        {previewData && (
          <div className="payroll-card">
            <div className="payroll-card-header">
              <h3>Employee Payroll Preview</h3>
            </div>

            <div className="payroll-table-wrap">
              <table className="payroll-table">
                <thead>
                  <tr>
                    <th>Employee</th>
                    <th>Gross</th>
                    <th>Deductions</th>
                    <th>Net</th>
                    <th>Status</th>
                  </tr>
                </thead>

                <tbody>
                  {previewData.employees.map((emp) => (
                    <tr
                      key={emp.employee_id}
                      style={{ cursor: "pointer" }}
                      onClick={() => setSelectedPreviewEmployee(emp)}
                    >
                      <td>
                        {emp.employee_code ? `${emp.employee_code} - ` : ""}
                        {emp.employee_name}
                      </td>
                      <td>{formatMoney(emp.gross_pay)}</td>
                      <td>{formatMoney(emp.total_deductions)}</td>
                      <td>{formatMoney(emp.net_pay)}</td>
                      <td>
                        {emp.warnings?.length ? (
                          <span className="pill warning">Issues</span>
                        ) : (
                          <span className="pill success">OK</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>

              {selectedPreviewEmployee && (
                <div className="payroll-card" style={{ marginTop: 16 }}>
                  <div className="payroll-card-header">
                    <div>
                      <h3>
                        Payroll Preview Details —{" "}
                        {selectedPreviewEmployee.employee_name}
                      </h3>
                      <p>
                        Detailed breakdown for this employee, including PAYE
                        computation and progressive tax bands.
                      </p>
                    </div>

                    <button
                      type="button"
                      className="ghost-btn"
                      onClick={() => setSelectedPreviewEmployee(null)}
                    >
                      Close
                    </button>
                  </div>

                  {selectedPreviewEmployee.warnings?.length ? (
                    <div className="payroll-alert warning">
                      <AlertTriangle size={16} />
                      {selectedPreviewEmployee.warnings.join(" | ")}
                    </div>
                  ) : null}

                  <div className="payroll-grid payroll-grid-4">
                    <div className="payroll-summary-card">
                      <p>Gross Pay</p>
                      <h3>{formatMoney(selectedPreviewEmployee.gross_pay)}</h3>
                    </div>

                    <div className="payroll-summary-card">
                      <p>Deductions</p>
                      <h3>
                        {formatMoney(selectedPreviewEmployee.total_deductions)}
                      </h3>
                    </div>

                    <div className="payroll-summary-card">
                      <p>Employer Contributions</p>
                      <h3>
                        {formatMoney(
                          selectedPreviewEmployee.employer_contributions
                        )}
                      </h3>
                    </div>

                    <div className="payroll-summary-card">
                      <p>Net Pay</p>
                      <h3>{formatMoney(selectedPreviewEmployee.net_pay)}</h3>
                    </div>
                  </div>

                  <div className="payroll-grid payroll-grid-2">
                    <div className="payroll-card">
                      <div className="payroll-card-header">
                        <h3>Computation Breakdown</h3>
                      </div>

                      <div className="payroll-table-wrap">
                        <table className="payroll-table">
                          <thead>
                            <tr>
                              <th>Component</th>
                              <th>Type</th>
                              <th>Amount</th>
                            </tr>
                          </thead>
                          <tbody>
                            {(selectedPreviewEmployee.breakdown || []).map(
                              (line, idx) => (
                                <tr key={`${line.component_code}-${idx}`}>
                                  <td>
                                    {line.component_name ||
                                      line.component_code ||
                                      "-"}
                                  </td>
                                  <td>{line.component_type || "-"}</td>
                                  <td>{formatMoney(line.amount)}</td>
                                </tr>
                              )
                            )}
                          </tbody>
                        </table>
                      </div>
                    </div>

                    <div>
                      <PayeBreakdownCard
                        payeLine={getPayeLine(selectedPreviewEmployee)}
                        title="PAYE Breakdown"
                      />
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        <div className="payroll-grid payroll-grid-4">
          <div className="payroll-mini-stat">
            <strong>Employees</strong>
            <span>{run?.employee_count || 0}</span>
          </div>
          <div className="payroll-mini-stat">
            <strong>Gross Total</strong>
            <span>{formatMoney(run?.gross_total || 0)}</span>
          </div>
          <div className="payroll-mini-stat">
            <strong>Deductions</strong>
            <span>{formatMoney(run?.deduction_total || 0)}</span>
          </div>
          <div className="payroll-mini-stat">
            <strong>Net Total</strong>
            <span>{formatMoney(run?.net_total || 0)}</span>
          </div>
        </div>
      </div>

      <div className="payroll-grid payroll-grid-2">
        <div className="payroll-card">
          <div className="payroll-card-header">
            <h3>Employees in Run</h3>

            <button
              type="button"
              className="primary-btn"
              onClick={handleBulkDownloadPayslips}
              disabled={bulkDownloading || !employees?.length}
            >
              <Download size={16} />
              {bulkDownloading ? "Preparing ZIP..." : "Download All Payslips"}
            </button>

            <div className="payroll-page-actions">
              <button
                type="button"
                className="secondary-btn"
                onClick={handlePreviewPayroll}
                disabled={previewing}
              >
                <Eye size={16} />
                {previewing ? "Previewing..." : "Preview Payroll"}
              </button>
            </div>
          </div>

          <div className="payroll-table-wrap">
            <table className="payroll-table">
              <thead>
                <tr>
                  <th>Employee</th>
                  <th>Gross</th>
                  <th>Deductions</th>
                  <th>Net</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {employees.map((row) => (
                  <tr key={row.id}>
                    <td>{row.employee_name}</td>
                    <td>{formatMoney(row.gross_pay)}</td>
                    <td>{formatMoney(row.total_deductions)}</td>
                    <td>{formatMoney(row.net_pay)}</td>
                    <td>
                      <button
                        type="button"
                        className="ghost-btn"
                        onClick={() => showLines(row)}
                      >
                        View Lines
                      </button>

                      <button
                        type="button"
                        className="ghost-btn"
                        onClick={() => handleDownloadPayslip(row)}
                      >
                        <FileDown size={16} />
                        Download Payslip
                      </button>

                      <button
                        type="button"
                        className="ghost-btn"
                        onClick={() => handleViewProcessedDetails(row)}
                      >
                        View Tax Details
                      </button>
                    </td>
                  </tr>
                ))}

                {!employees.length && (
                  <tr>
                    <td colSpan="5">No employees found for this run.</td>
                  </tr>
                )}
              </tbody>
            </table>

            {selectedProcessedEmployee && (
              <div className="payroll-card" style={{ marginTop: 16 }}>
                <div className="payroll-card-header">
                  <div>
                    <h3>
                      Processed Payroll Details —{" "}
                      {selectedProcessedEmployee.employee_name}
                    </h3>
                    <p>
                      Actual processed breakdown, including PAYE computation
                      details.
                    </p>
                  </div>

                  <button
                    type="button"
                    className="ghost-btn"
                    onClick={() => {
                      setSelectedProcessedEmployee(null);
                      setSelectedProcessedBreakdown([]);
                    }}
                  >
                    Close
                  </button>
                </div>

                <div className="payroll-grid payroll-grid-4">
                  <div className="payroll-summary-card">
                    <p>Gross Pay</p>
                    <h3>{formatMoney(selectedProcessedEmployee.gross_pay)}</h3>
                  </div>

                  <div className="payroll-summary-card">
                    <p>Deductions</p>
                    <h3>
                      {formatMoney(selectedProcessedEmployee.total_deductions)}
                    </h3>
                  </div>

                  <div className="payroll-summary-card">
                    <p>Employer Contributions</p>
                    <h3>
                      {formatMoney(
                        selectedProcessedEmployee.employer_contributions
                      )}
                    </h3>
                  </div>

                  <div className="payroll-summary-card">
                    <p>Net Pay</p>
                    <h3>{formatMoney(selectedProcessedEmployee.net_pay)}</h3>
                  </div>
                </div>

                <div className="payroll-grid payroll-grid-2">
                  <div className="payroll-card">
                    <div className="payroll-card-header">
                      <h3>Processed Breakdown</h3>
                    </div>

                    <div className="payroll-table-wrap">
                      <table className="payroll-table">
                        <thead>
                          <tr>
                            <th>Component</th>
                            <th>Type</th>
                            <th>Amount</th>
                          </tr>
                        </thead>
                        <tbody>
                          {selectedProcessedBreakdown.map((line, idx) => (
                            <tr key={`${line.component_code}-${idx}`}>
                              <td>
                                {line.component_name ||
                                  line.component_code ||
                                  "-"}
                              </td>
                              <td>{line.component_type || "-"}</td>
                              <td>{formatMoney(line.amount)}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>

                  <div>
                    <PayeBreakdownCard
                      payeLine={getProcessedPayeLine(
                        selectedProcessedBreakdown
                      )}
                      title="PAYE Breakdown"
                    />
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        <div className="payroll-card">
          <div className="payroll-card-header">
            <h3>
              {selectedEmployee
                ? `Pay Breakdown — ${selectedEmployee.employee_name}`
                : "Select an employee"}
            </h3>
          </div>

          <div className="payroll-table-wrap">
            <table className="payroll-table">
              <thead>
                <tr>
                  <th>Component</th>
                  <th>Type</th>
                  <th>Amount</th>
                </tr>
              </thead>
              <tbody>
                {lines.map((line) => (
                  <tr key={line.id}>
                    <td>{line.component_name}</td>
                    <td>{line.component_type}</td>
                    <td>{formatMoney(line.amount)}</td>
                  </tr>
                ))}
                {!lines.length && (
                  <tr>
                    <td colSpan="3">No payroll lines yet.</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}
